import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useFocusTrap } from '../../ui/components/useFocusTrap';
import dialogStyles from '../../ui/components/Dialog.module.css';
import styles from './Workout.module.css';

/**
 * Remove-exercise confirmation. Same shape as `AbandonConfirmDialog` (own
 * per-feature copy, shared `Dialog.module.css` visuals): removal tombstones
 * the exercise and everything logged under it, so it is always confirmed.
 * Worded "Remove", never "Delete" — the Exercise Library entry and the
 * Training Plan are untouched; only this workout loses the exercise.
 */
export function RemoveExerciseDialog({
  hasCompletedWork,
  onConfirm,
  onCancel,
}: {
  hasCompletedWork: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const cancelRef = useRef<HTMLButtonElement | null>(null);

  useFocusTrap(panelRef);

  useEffect(() => {
    // Cancel, not the panel: the safe choice holds initial focus, so an
    // accidental Enter/Space dismisses rather than removes.
    cancelRef.current?.focus();
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') onCancel();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  return createPortal(
    <div className={dialogStyles.dialogOverlay} onClick={onCancel}>
      <div
        ref={panelRef}
        className={dialogStyles.dialogPanel}
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="remove-exercise-dialog-title"
        aria-describedby="remove-exercise-dialog-body"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="remove-exercise-dialog-title" className={dialogStyles.dialogTitleCompact}>
          Remove exercise?
        </h2>
        <p id="remove-exercise-dialog-body" className={styles.stateText}>
          {hasCompletedWork
            ? 'This exercise and its completed work will be removed from this workout and will not appear in workout history. Your Training Plan will not be changed.'
            : 'This exercise will be removed from this workout. Your Training Plan will not be changed.'}
        </p>

        <div className={styles.actions}>
          <button type="button" className={`${styles.actionButton} ${styles.actionDangerSolid}`} onClick={onConfirm}>
            Remove exercise
          </button>
          <button ref={cancelRef} type="button" className={styles.actionButton} onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
